// Copyright (c) 2025, WSO2 LLC. (https://www.wso2.com). All Rights Reserved.
//
// This software is the property of WSO2 LLC. and its suppliers, if any.
// Dissemination of any information or reproduction of any material contained
// herein in any form is strictly forbidden, unless permitted by WSO2 expressly.
// You may not alter or remove any copyright or other notice from copies of this content.
import React, { useEffect, useState } from "react";
import { Link as RouterLink, NavLink } from "react-router-dom";
import {
  Container,
  Chip,
  Grid,
  Button,
  IconButton,
  Typography,
  Snackbar,
  Backdrop,
  CircularProgress,
} from "@mui/material";
import { makeStyles } from "tss-react/mui";
import { AppConfig, roles } from "../../../../configs/admin-config";
import { checkIfRoleExists, getAccessToken } from "../../../../utils/oauth";
import LinkOutlinedIcon from "@mui/icons-material/LinkOutlined";
import MuiAlert from "@mui/material/Alert";
import { useHistory } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ConfirmationDialog from "../../Dialog/ConfirmationDialog";

const useStyles = makeStyles()((theme) => ({
  backdrop: {
    zIndex: 2,
    color: "white",
  },
  formStatusGrid: {
    marginTop: 88,
  },
  buttonGrid: {
    textAlign: "end",
  },
  button: {
    textTransform: "none",
  },
  Typography: {
    marginTop: 50,
    textAlign: "center",
  },
}));

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} variant="filled" ref={ref} {...props} />;
});

const DeactivatedPage = (props) => {
  const history = useHistory();
  const { classes } = useStyles();
  const [copySnack, setCopy] = useState(false);
  const [successSnack, setSuccess] = useState(false);
  const [failSnack, setFail] = useState(false);
  const [backdropOpen, setbackDrop] = useState(false);
  const [confirmationDialog, setConfirmationDialog] = useState(null);

  const handleConfirmationDialog = (content) => {
    setConfirmationDialog(content);
  };

  const handleChangeStatus = () => {
    handleConfirmationDialog({
      title: "You are about to make a change to a reseller!",
      text: "Are you sure you want to activate this reseller?",
      confirmAction: () => {
        changeStatus("active");
      },
    });
  };

  const changeStatus = async (newStatus) => {
    setbackDrop(true);

    const requestURL = AppConfig.baseUrl + AppConfig.tradeReferenceFormStatus;

    let data = {
      linkId: parseInt(props.location.state.item.linkId),
      formStatus: newStatus,
      clientUrl: props.location.state.item.clientUrl || "",
    };

    try {
      let response = await fetch(requestURL, {
        method: "PATCH",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: "Bearer " + getAccessToken(),
        },
        body: JSON.stringify(data),
      });
      let body = await response.json();
      setbackDrop(false);
      if (response.status === 200) {
        setSuccess(true);
        const tradeRef = props.location.state.item;
        if (newStatus === "active") {
          history.push({
            pathname: AppConfig.urls.ADMIN + AppConfig.urls.PENDING_PAGE_2 + tradeRef.linkId,
            state: props.location.state
          });
        }
      } else {
        console.error(body.message);
        setFail(true);
      }
      return null;
    } catch (error) {
      console.error(error);
      setbackDrop(false);
      setFail(true);
      return error;
    }
  };

  const handleClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setSuccess(false);
    setFail(false);
    setCopy(false);
  };

  return (
    <>
      <Container>
        <Backdrop open={backdropOpen} className={classes.backdrop}>
          <CircularProgress color="secondary" />
        </Backdrop>

        <Snackbar
          open={successSnack}
          autoHideDuration={3000}
          onClose={handleClose}
         
        >
          <Alert onClose={handleClose} severity="success">
            Success!
          </Alert>
        </Snackbar>
        <Snackbar
          open={copySnack}
          autoHideDuration={3000}
          onClose={handleClose}
         
        >
          <Alert onClose={handleClose} severity="success">
            Copied to Clipboard!
          </Alert>
        </Snackbar>
        <Snackbar
          open={failSnack}
          autoHideDuration={3000}
          onClose={handleClose}
         
        >
          <Alert onClose={handleClose} severity="error">
            Server Error!
          </Alert>
        </Snackbar>

        <Grid container spacing={1} className={classes.formStatusGrid}>
          <Grid item xs={12}>
            <Grid
              container
              direction="row"
              justifycontent="flex-start"
              alignItems="center"
            >
              <Grid item>
                <NavLink to={AppConfig.urls.ADMIN + "home/tradereferences"}>
                  <IconButton aria-label="back">
                    <ArrowBackIcon />
                  </IconButton>
                </NavLink>
              </Grid>
            </Grid>
          </Grid>
          <Grid
            item
            xs={12}
            md={6}
            lg={8}
            container
            direction="row"
            justifycontent="flex-start"
            alignItems="center"
          >
            <Chip
              variant="outlined"
              label={"Form Status: Deactivated"}
              color={"primary"}
            />
          </Grid>
          {(checkIfRoleExists(roles.admin) ||
            checkIfRoleExists(roles.channelManager)) && (
            <Grid item xs={12} md={3} lg={4} className={classes.buttonGrid}>
              <Button
                className={classes.button}
                variant="outlined"
                color="primary"
                onClick={handleChangeStatus}
              >
                Activate
              </Button>
            </Grid>
          )}
        </Grid>

        <Typography className={classes.Typography}>
          {props.location.state && props.location.state.item.companyName} has
          been deactivated.
        </Typography>
      </Container>
      {confirmationDialog && (
        <ConfirmationDialog
          content={confirmationDialog}
          handleOpen={handleConfirmationDialog}
        />
      )}
    </>
  );
};

export default DeactivatedPage;
